import type { FC } from "react";
import type { LocationPlatformStatus, MasterLocationDto } from "shared";
import { Button } from "../components/Button";
import {
	useMasterLocation,
	useMasterLocationActions,
} from "../settings/useIntegrations";

const STATUS_LABEL: Record<LocationPlatformStatus, string> = {
	connected: "Connected",
	synced: "Synced",
	pending: "Pending",
	drift: "Drift",
	error: "Error",
	not_claimed: "Not claimed",
};

const STATUS_COLOR: Record<LocationPlatformStatus, string> = {
	connected: "#0d6efd",
	synced: "#198754",
	pending: "#6c757d",
	drift: "#fd7e14",
	error: "#dc3545",
	not_claimed: "#adb5bd",
};

function platformLabel(platform: string) {
	if (platform === "google") return "Google";
	if (platform === "facebook") return "Facebook";
	return platform;
}

type ChipProps = {
	platform: string;
	status: LocationPlatformStatus;
	driftedFields?: string[];
	lastError?: string | null;
};

const StatusChip: FC<ChipProps> = ({
	platform,
	status,
	driftedFields,
	lastError,
}) => {
	const title =
		status === "drift" && driftedFields?.length
			? `Differs: ${driftedFields.join(", ")}`
			: lastError || STATUS_LABEL[status];

	return (
		<span
			title={title}
			style={{
				display: "inline-flex",
				alignItems: "center",
				gap: 6,
				padding: "4px 10px",
				borderRadius: 999,
				fontSize: 12,
				fontWeight: 600,
				background: `${STATUS_COLOR[status]}22`,
				color: STATUS_COLOR[status],
				border: `1px solid ${STATUS_COLOR[status]}55`,
			}}
		>
			<span
				style={{
					width: 8,
					height: 8,
					borderRadius: "50%",
					background: STATUS_COLOR[status],
				}}
			/>
			{platformLabel(platform)}: {STATUS_LABEL[status]}
		</span>
	);
};

export const PlatformStatusBar: FC<{ locationId?: string }> = ({
	locationId,
}) => {
	const master = useMasterLocation(locationId);
	const { checkDrift, publishEverywhere, syncWebsiteSchema } =
		useMasterLocationActions(locationId);

	if (!master.data) {
		return null;
	}

	return (
		<div
			style={{
				display: "flex",
				alignItems: "center",
				gap: 8,
				flexWrap: "wrap",
				marginBottom: 12,
			}}
		>
			{(master.data as MasterLocationDto).platforms.map((p) => (
				<StatusChip
					key={p.platform}
					platform={p.platform}
					status={p.status}
					driftedFields={p.driftedFields}
					lastError={p.lastError}
				/>
			))}
			<Button
				variant="ghost"
				size="sm"
				icon="cloud_upload"
				loading={publishEverywhere.isPending}
				onClick={() => publishEverywhere.mutate()}
			>
				Publish everywhere
			</Button>
			<Button
				variant="ghost"
				size="sm"
				icon="sync"
				loading={checkDrift.isPending}
				onClick={() => checkDrift.mutate()}
			>
				Check drift
			</Button>
			<Button
				variant="ghost"
				size="sm"
				icon="public"
				loading={syncWebsiteSchema.isPending}
				onClick={() => syncWebsiteSchema.mutate()}
				title="Push LocalBusiness JSON-LD to WordPress / Wix"
			>
				Sync website schema
			</Button>
		</div>
	);
};
